<template>
  <div class="page-talking">
    <header>
      <h1>{{ roomName }}</h1>
      <span class="duration">{{ talkingDuration }}</span>
      <SignalIcon v-model="networkQuality" class="signal" v-if="serverProvidedConfig.engine !== 3"/>
    </header>
    <section class="force-control" v-if="showForceControl">
      <div class="container">
        <img class="reduce" @click="changeCameraForce('reduce')">
        <img class="amplify" @click="changeCameraForce('amplify')">
      </div>
    </section>
    <a-row type="flex" class="fill-remaining-height">
      <a-col flex="1 1">
        <main>

          <section v-if="showLoading" class="loading-rtc">
            <div class="loading-animation"></div>
            <span>{{$t('calling.EnteringCollaboration')}}...</span>
          </section>

          <div v-if="!showLoading && (!videoOnMembers || videoOnMembers.length === 0)" class="self-avatar-container">
            <UserAvatar :user-avatar="localAvatar"/>

            <section class="user-tips">
              <i class="volume-off"></i>
              <i class="volume" :style="{backgroundPositionY: audioVolume[localUser.id] + 'px'}"></i>
              <a-tooltip>
                <template v-if="String(localUser.name).length > 4" slot="title">
                  {{ localUser.name }}
                </template>
                <span class="name">{{ localUser.name | maxLengthName }}</span>
              </a-tooltip>
            </section>
          </div>

          <div v-show="!showLoading" class="video-container" :class="[`have-${matrix}-video`]">

            <div
                v-for="member in videoOnMembers"
                :key="member.id"
                :id="`video-player-${member.id}`"
                class="video-player"
                :style="videoPlayerStyle"
                @dblclick="playBigVideo(member)">
              <div class="screen-sharing-tip" v-if="member.id === localUser.id && localScreenSharingOn">
                <SvgIcon icon-class="talking-screen-sharing" />
                <span>{{$t('calling.ScreenSharing')}}...</span>
              </div>
              <section class="user-tips">
                <i class="volume-off"></i>
                <i class="volume" :style="{backgroundPositionY: audioVolume[member.id] + 'px'}"></i>
                <a-tooltip>
                  <template v-if="String(member.name).length > 4" slot="title">
                    {{ member.name }}
                  </template>
                  <span class="name">{{ member.name | maxLengthName }}</span>
                </a-tooltip>
              </section>
            </div>
          </div>

          <div v-show="!!bigVideoUser" class="big-video-container" @dblclick="stopBigVideo">
            <div id="big-video-player" :style="singleVideoOrFreezeStyle" class="video-player">
              <div class="screen-sharing-tip" v-if="bigVideoUser && bigVideoUser.id === localUser.id && localScreenSharingOn">
                <SvgIcon icon-class="talking-screen-sharing" />
                <span>{{$t('calling.ScreenSharing')}}...</span>
              </div>
              <section v-if="!!bigVideoUser" class="user-tips">
                <i class="volume-off"></i>
                <i class="volume" :style="{backgroundPositionY: audioVolume[bigVideoUser.id] + 'px'}"></i>
                <a-tooltip>
                  <template v-if="String(bigVideoUser.name).length > 4" slot="title">
                    {{ bigVideoUser.name }}
                  </template>
                  <span class="name">{{ bigVideoUser.name | maxLengthName }}</span>
                </a-tooltip>
              </section>
            </div>
          </div>

          <DrawInFreeze
            v-show="showFreeze"
            ref="drawInFreeze"
            class="freeze-container"
            :container-style="singleVideoOrFreezeStyle"
            :operations-style="drawOperationsStyle"
            :base-map="freezeBaseMap"
            :lines-data="linesData"
            :freeze-user-name="freezeOnUser ? freezeOnUser.name : ''"
            @draw-line-over="line => localDrawLine(line, 'freeze')"
            @undo-draw="_ => localUndoDraw('freeze')"
            @clear-draw="_ => localClearDraw('freeze')"
            @close-draw="toggleFreeze"
          ></DrawInFreeze>

          <DrawInWhiteboard
            v-show="showWhiteBoard"
            ref="drawInWhiteBoard"
            class="whiteboard-container"
            :container-style="singleVideoOrFreezeStyle"
            :operations-style="drawOperationsStyle"
            :lines-data="whiteBoardLinesData"
            :white-board-image="whiteBoardImage"
            :freeze-user-name="whiteBoardOnUser ? whiteBoardOnUser.name : ''"
            @draw-line-over="line => localDrawLine(line, 'whiteBoard')"
            @undo-draw="_ => localUndoDraw('whiteBoard')"
            @clear-draw="_ => localClearDraw('whiteBoard')"
            @close-draw="toggleWhiteBoard"
            @sticker-draw="localStickerDraw"
          ></DrawInWhiteboard>

          <RealtimeMark
            v-show="showRealtimeMark"
            class="realtime-mark-container"
            :container-style="singleVideoOrFreezeStyle"
            :point-list="realtimeMarkList"
            @generate-mark="drawLocalRealtimeMark"
            @close-mark="toggleRealtimeMark"
          ></RealtimeMark>

          <SlamMark
            v-show="showSlamMark"
            class="slam-mark-container"
            :container-style="singleVideoOrFreezeStyle"
            @generate-slam-mark="drawLocalSlamMark"
            @undo-slam-mark="localUndoSlamMark"
            @clear-slam-mark="localClearSlamMark"
            @close-slam-mark="toggleSlamMark"></SlamMark>
        </main>
      </a-col>
      <transition name="drawer">
        <a-col flex="0 1" v-if="show_members" id="session-members">
          <SessionMember :all-avatar="avatarMap" @hide="show_members = false"/>
        </a-col>
      </transition>
      <transition name="drawer">
        <a-col flex="0 1" v-if="show_files" id="file-manager">
          <FileManager @hide="show_files = false"/>
        </a-col>
      </transition>
    </a-row>

    <footer>
      <ul class="operations">
        <li class="operation-btn">
          <button class="icon-wrapper" @click="toggleCamera">
            <SvgIcon :icon-class="localCameraOn ? 'talking-camera-on' : 'talking-camera-off'" />
          </button>
          <div>{{$t('calling.Camera')}}</div>
        </li>

        <li class="operation-btn">
          <button class="icon-wrapper " @click="toggleMicrophoneBeforeMqtt">
            <SvgIcon :icon-class="localMicrophoneOn ? 'talking-microphone-on' : 'talking-microphone-off'" />
          </button>
          <div>{{$t('calling.Microphone')}}</div>
        </li>

        <li class="operation-btn">
          <button class="icon-wrapper" @click="toggleSpeaker">
            <SvgIcon :icon-class="localSpeakerOn ? 'talking-speaker-on' : 'talking-speaker-off'" />
          </button>
          <div>{{$t('calling.Speaker')}}</div>
        </li>

        <li class="operation-btn" :class="[showSlamMark && !!slamMarkOnUser ? 'on' : '', slamMarkDisabled ? 'disabled' : '']">
          <button class="icon-wrapper" @click="toggleSlamMark">
            <SvgIcon :icon-class="showSlamMark && slamMarkOnUser ? 'talking-slam-mark-on' : 'talking-slam-mark-off'" />
          </button>
          <div>{{$t('calling.Sticker')}}</div>
        </li>

        <li class="operation-btn" :class="[showRealtimeMark && !!realtimeMarkOnUser ? 'on' : '', realtimeMarkDisabled ? 'disabled' : '']">
          <button class="icon-wrapper" @click="toggleRealtimeMark">
            <SvgIcon :icon-class="showRealtimeMark && realtimeMarkOnUser ? 'talking-realtime-mark-on' : 'talking-realtime-mark-off'" />
          </button>
          <div>{{$t('calling.LaserPointer')}}</div>
        </li>

        <li class="operation-btn" :class="[showFreeze && !!freezeOnUser ? 'on' : '', freezeDisabled ? 'disabled' : '']">
          <button class="icon-wrapper" @click="toggleFreeze">
            <SvgIcon :icon-class="showFreeze && freezeOnUser ? 'talking-freeze-on' : 'talking-freeze-off'" />
          </button>
          <div>{{$t('calling.FreezeScreen')}}</div>
        </li>

        <li class="operation-btn" :class="[showWhiteBoard && !!whiteBoardOnUser ? 'on' : '', whiteBoardDisabled ? 'disabled' : '']">
          <button class="icon-wrapper" @click="toggleWhiteBoard">
            <SvgIcon :icon-class="showWhiteBoard && whiteBoardOnUser ? 'talking-whiteboard-on' : 'talking-whiteboard-off'" />
          </button>
          <div>{{$t('calling.WhiteBoard')}}</div>
        </li>

        <li class="operation-btn" :class="[localScreenSharingOn ? 'on' : '', screenSharingDisabled ? 'disabled' : '']">
          <button class="icon-wrapper" @click="toggleScreenSharing">
            <SvgIcon icon-class="talking-screen-sharing" />
          </button>
          <div>{{$t('calling.ScreenShare')}}</div>
        </li>

        <li class="operation-btn file-manager-control" :class="[show_files ? 'on' : '', fileDisabled ? 'disabled' : '']">
          <button class="icon-wrapper" @click="toggleFiles">
            <SvgIcon icon-class="talking-file" />
          </button>
          <div>{{$t('home.File')}}</div>
          <div class="count" v-if="!show_files && notReadFileCount">{{notReadFileCount > 99 ? '99+' : notReadFileCount}}</div>
        </li>

        <li class="operation-btn" :class="[show_members ? 'on' : '', memberDisabled ? 'disabled' : '']">
          <a-tooltip :visible="showMemberTip" overlayClassName="member-tip" :getPopupContainer="memberTipContainer">
            <template slot="title">
              {{$t('calling.AddMemberFormMember')}}
            </template>
            <div></div>
          </a-tooltip>
          <button class="icon-wrapper" @click="toggleMembers">
            <SvgIcon icon-class="talking-member" />
          </button>
          <div>{{$t('calling.Members')}}</div>
        </li>

        <li class="operation-btn hang-up">
          <button class="icon-wrapper" @click="beforeHangUp">
            <SvgIcon icon-class="talking-hangup" />
          </button>
          <div>{{$t('home.HangUp')}}</div>
        </li>
      </ul>

      <!-- <div class="operation-btn hang-up">
        <button class="icon-wrapper" @click="beforeHangUp">
          <SvgIcon icon-class="talking-hangup" />
        </button>
        <div>{{$t('home.HangUp')}}</div>
      </div> -->
    </footer>

    <a-modal
      :visible="checkRoleModalVisible"
      :closable="false"
      :maskClosable="false"
      :keyboard="false"
      :width="460"
      :footer="null"
      :getContainer="modalContainer"
      centered
      class="two-buttons-one-close-confirm-modal"
    >
      <p class="custom-content">
        <span>{{$t('calling.ApplyLabelingPermission')}}</span>
        <i @click="checkRoleModalVisible = false"></i>

        <config-provider :autoInsertSpaceInButton="false">
          <BasicBtnGroup
            :primaryContent="$t('common.Ok')"
            @primeClick="applyRole"
            @defClick="checkRoleModalVisible = false"
          ></BasicBtnGroup>
        </config-provider>
      </p>
    </a-modal>

    <a-modal
      :visible="!!someoneApplyRole"
      :closable="false"
      :maskClosable="false"
      :keyboard="false"
      :width="460"
      :footer="null"
      :getContainer="modalContainer"
      centered
      class="two-buttons-one-close-confirm-modal"
    >
      <p class="custom-content">
        <span>"{{ someoneApplyRole ? someoneApplyRole.userName : '' }}"{{$t('common.Apply')}}{{ someoneApplyRole ? someoneApplyRole.roleName : '' }}{{$t('common.Permission')}}</span>
        <i @click="rejectApplyRole"></i>

        <config-provider :autoInsertSpaceInButton="false">
          <BasicBtnGroup
            :primaryContent="$t('common.Agree')"
            :defContent="$t('common.Deny')"
            @primeClick="grantRole"
            @defClick="rejectApplyRole"
          ></BasicBtnGroup>
        </config-provider>
      </p>
    </a-modal>

    <a-modal
      :visible="conflictModalVisible"
      :closable="false"
      :maskClosable="false"
      :keyboard="false"
      :width="460"
      :footer="null"
      :getContainer="modalContainer"
      centered
      class="two-buttons-one-close-confirm-modal"
    >
      <p class="custom-content">
        <span>{{$t('calling.CameraAndShareNotUseOnSameTime')}}, {{ screenCameraConflict === 'camera' ? $t('calling.TurnoffCameraAndOnShare') : $t('calling.TurnoffShareAndOnCamera') }}？</span>
        <i @click="resetScreenCameraConflict"></i>

        <config-provider :autoInsertSpaceInButton="false">
          <BasicBtnGroup
            :primaryContent="$t('common.Ok')"
            @primeClick="resolveScreenCameraConflict"
            @defClick="resetScreenCameraConflict"
          ></BasicBtnGroup>
        </config-provider>
      </p>
    </a-modal>

    <a-modal
      :visible="localScreenSharingWarningVisible"
      :closable="false"
      :maskClosable="false"
      :keyboard="false"
      :width="460"
      :footer="null"
      :getContainer="modalContainer"
      centered
      class="screen-warning two-buttons-one-close-confirm-modal"
    >
      <p class="custom-content">
        <span>{{$t('calling.SureShare')}}</span>

        <config-provider :autoInsertSpaceInButton="false">
          <BasicBtnGroup
            :primaryContent="$t('common.Ok')"
            @primeClick="confirmScreenWarning"
            @defClick="cancelScreenWarning"
          ></BasicBtnGroup>
        </config-provider>
      </p>
    </a-modal>

    <a-modal
      :visible="leaveWarningVisible"
      :closable="false"
      :maskClosable="false"
      :keyboard="false"
      :width="460"
      :footer="null"
      :getContainer="modalContainer"
      centered
      class="two-buttons-one-close-confirm-modal"
    >
      <p class="custom-content">
        <span>{{$t('calling.HungUpOfLeaveCollaboration')}}</span>
        <i @click="leaveWarningVisible = false"></i>

        <config-provider :autoInsertSpaceInButton="false">
          <BasicBtnGroup
            :primaryContent="$t('calling.SureLeave')"
            @primeClick="hangUp"
            @defClick="leaveWarningVisible = false"
          ></BasicBtnGroup>
        </config-provider>
      </p>
    </a-modal>

    <a-modal
      :visible="hostLeaveWarningVisible"
      :closable="false"
      :maskClosable="false"
      :keyboard="false"
      :width="540"
      :footer="null"
      :getContainer="modalContainer"
      centered
      class="host-leave-warning"
    >
      <section class="custom-content">
        <h2>
          <span>{{$t('calling.EndCollaboration')}}</span>
          <i @click="hostLeaveWarningVisible = false"></i>
        </h2>

        <p>
          <span>{{$t('calling.LeaveCollaborationToast')}}</span>
          <br>
          <span>{{$t('calling.EndCollaborationToast')}}</span>

          <config-provider :autoInsertSpaceInButton="false">
            <BasicBtnGroup
              :primaryContent="$t('calling.EndCollaboration')"
              :sub-primary-content="$t('calling.LeaveCollaboration')"
              :show-sub-prime="true"
              @primeClick="stopConference"
              @subPrimeClick="hangUp"
              @defClick="hostLeaveWarningVisible = false"
            ></BasicBtnGroup>
          </config-provider>
        </p>
      </section>
    </a-modal>
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex'
import lottie from 'lottie-web'
import mqttcomm from '@/mqttcomm'
import Global from '@/global'
import SessionMember from '@/components/business/session-member'
import FileManager from '@/components/business/file-manager'
import DrawInFreeze from '@/components/business/draw-in-freeze'
import DrawInWhiteboard from '@/components/business/draw-in-whiteboard'
import { uploadFile, stopConference } from '@/api/talking'
import UserAvatar from '@/components/business/avatar/UserAvatar.vue'
import { CACHE_KEY, PAGE_NAME } from '@/utils/constant'
import BasicBtnGroup from '@/components/behavior/basic-btn-group'
import { ConfigProvider, Modal } from 'ant-design-vue'
import RealtimeMark from '@/components/business/realtime-mark'
import SlamMark from '@/components/business/slam-mark'
import { debounce, doubleCheck } from '@/utils/ops'
import rtc from '@/rtccomm'
import { EDeviceType } from '@/pb/status_entity_pb'
import { microPhoneAuthCheck } from '@/utils/microPhoneAuthCheck'
import SignalIcon from '@/components/business/signal-icon'
import { addAttachFiles } from '@/api/coc'
import { ESig } from '@/pb/signaling_param_pb'
import forceAmplify from '@/assets/talking/force-amplify.png'
import forceClose from '@/assets/talking/force-close.png'

let loadingAnimation
let videoContainerResizeObserver
let videoMainElWidth
let videoMainElHeight
let checkVolumeAnimation

// 生成所有用户的头像信息
function generateAvatarMap (orgList, avatarMap) {
  orgList.forEach(org => {
    if (org.avatar) {
      avatarMap[org.orgId] = org.avatar
    }
    generateAvatarMap(org.orgList, avatarMap)
  })
}
function isMobileClient (deviceType) {
  return deviceType === EDeviceType.IOS ||
    deviceType === EDeviceType.ANDROID ||
    deviceType === EDeviceType.GLASSES ||
    deviceType === EDeviceType.H100 ||
    deviceType === EDeviceType.CIMO ||
    deviceType === EDeviceType.WU_KONG
}

let preFreezeUserId = ''
let forceUvalue = 0
export default {
  name: 'Talking',
  components: {
    SessionMember,
    FileManager,
    DrawInFreeze,
    DrawInWhiteboard,
    UserAvatar,
    BasicBtnGroup,
    ConfigProvider,
    RealtimeMark,
    SlamMark,
    SignalIcon
  },

  filters: {
    maxLengthName (value) {
      const name = String(value)
      return name.length > 4 ? name.slice(0, 3) + '...' : name
    }
  },

  data() {
    return {
      talkingDuration: '00:00:00',
      talkingDurationInterval: undefined,
      resizeHandler: undefined,
      videoPlayerStyle: {},
      singleVideoOrFreezeStyle: {},
      show_members: false,
      show_files: false,
      showFreeze: false,
      showWhiteBoard: false,
      freezeBaseMap: undefined,
      showRealtimeMark: false,
      showSlamMark: false,
      checkRoleModalVisible: false,
      drawOperationsStyle: {},
      audioVolume: {},
      showLoading: true,
      showMemberTip: false,
      avatarMap: {},
      leaveFromHangup: false,
      screenCameraConflict: false,
      conflictModalVisible: false,
      debouncedClearPoint: undefined,
      leaveWarningVisible: false,
      hostLeaveWarningVisible: false,
      visibleForce: false,
      forceClose,
      forceAmplify,
      matrix: 1
    }
  },

  computed: {
    ...mapState([
      'localScreenSharingWarningVisible',
      'realtimeMarkList',
      'sessionMediaType',
      'sessionMediaClient',
      'networkQuality',
      'serverProvidedConfig',
      'sessionMembers'
    ]),
    ...mapState('coc', ['notReadFileCount']),
    ...mapGetters('coc', ['currentCoc']),
    ...mapGetters([
      'localUser',
      'mediaOnMembers',
      'mediaOffMembers',
      'bigVideoUser',
      'freezeOnUser',
      'whiteBoardOnUser',
      'slamMarkOnUser',
      'localScreenSharingOn',
      'localScreenSharingOff',
      'localScreenSharingPending',
      'otherScreenSharingOnUser',
      'realtimeMarkOnUser'
    ]),
    videoOnMembers() {
      return this.mediaOnMembers.filter(member => member.cameraOn && member.id !== this.localUser.id)
        .concat(this.localCameraOn || this.localScreenSharingOn ? [this.localUser] : [])
    },
    microphoneOnMember () {
      return this.mediaOnMembers.filter(member => member.microphoneOn)
    },
    localMicrophoneOn() {
      return this.localUser.microphoneOn
    },
    localCameraOn() {
      return this.localUser.cameraOn && !this.localScreenSharingOn
    },
    localSpeakerOn() {
      return this.localUser.speakerOn
    },
    localAvatar () {
      return this.avatarMap[this.localUser.id] || this.localUser.avatarUrl
    },
    localBigVideo () {
      return this.localUser.isPlayBigVideo
    },
    localWhiteBoardOn () {
      return this.localUser.whiteBoardOn
    },
    localFreezeOn () {
      return this.localUser.freezeOn
    },
    localRealtimeMarkOn () {
      return this.localUser.realtimeMarkOn
    },
    localSlamMarkOn () {
      return this.localUser.slamMarkOn
    },
    roomName() {
      return this.$store.state.currentSession?.roomName
    },
    linesData () {
      return this.$store.state.linesData
    },
    whiteBoardLinesData () {
      return this.$store.state.whiteBoardLinesData
    },
    whiteBoardImage () {
      return this.$store.state.whiteBoardImage
    },
    someoneApplyRole () {
      return this.$store.state.someoneApplyRole
    },
    slamMarkDisabled () {
      // this.slamMarkOnUser         是否有人在万事贴
      // this.bigVideoUser           大屏用户
      // this.videoOnMembers         开启视频的用户包括自己开摄像头和自己开分享
      // this.localScreenSharingOff  自己开启分享
      // this.realtimeMarkOnUser     是否有人在使用激光笔
      // this.freezeOnUser           是否有人在冻屏
      // this.whiteBoardOnUser       是否有人在白板
      // 设置其他设备时禁用按钮
      let deviceAllow = false
      if (this.bigVideoUser){
        const rVD = this.bigVideoUser.deviceType
        if (!isMobileClient(rVD)) {
          deviceAllow = true
        }
      }

      return (!this.slamMarkOnUser && (!this.bigVideoUser || deviceAllow) && this.videoOnMembers.length !== 1) ||
        !this.localScreenSharingOff ||
        !!this.otherScreenSharingOnUser ||
        !!this.realtimeMarkOnUser ||
        !!this.freezeOnUser ||
        !!this.whiteBoardOnUser
    },
    // 白板按钮禁用
    whiteBoardDisabled () {
      return !this.localScreenSharingOff ||
        !!this.otherScreenSharingOnUser ||
        !!this.freezeOnUser ||
        !!this.realtimeMarkOnUser ||
        !!this.slamMarkOnUser
    },
    freezeDisabled () {
      return (!this.freezeOnUser && !this.bigVideoUser && this.videoOnMembers.length !== 1) ||
        !this.localScreenSharingOff ||
        !!this.otherScreenSharingOnUser ||
        !!this.realtimeMarkOnUser ||
        !!this.slamMarkOnUser ||
        !!this.whiteBoardOnUser
    },
    realtimeMarkDisabled () {
      return (!this.realtimeMarkOnUser && !this.bigVideoUser && this.videoOnMembers.length !== 1) ||
        !this.localScreenSharingOff ||
        !!this.otherScreenSharingOnUser ||
        !!this.freezeOnUser ||
        !!this.slamMarkOnUser ||
        !!this.whiteBoardOnUser
    },
    fileDisabled () {
      return this.showWhiteBoard || this.showFreeze || this.showRealtimeMark || this.showSlamMark
    },
    memberDisabled () {
      return this.showWhiteBoard || this.showFreeze || this.showRealtimeMark || this.showSlamMark
    },
    screenSharingDisabled () {
      return !!this.otherScreenSharingOnUser || !!this.freezeOnUser || !!this.whiteBoardOnUser || !!this.realtimeMarkOnUser || !!this.slamMarkOnUser
    },
    localVideoBeUsedToRealtimeMark () {
      return this.realtimeMarkOnUser && +this.localUser.id === +this.realtimeMarkOnUser.realtimeMarkTargetUserId
    },
    conferenceIdOnRouter () {
      return this.$router.currentRoute.params.conferenceId
    },
    showForceControl(){
      // 假大屏逻辑-videoOnMembers 仅仅只有一个元素
      const falseLargeScreen = this.videoOnMembers.length === 1 && this.isForceMobileClient(this.videoOnMembers[0].deviceType)

      // 真大屏逻辑-bigVideoUser 有值
      const trueLyLargeScreen = !!this.bigVideoUser && this.isForceMobileClient(this.bigVideoUser.deviceType)

      let member = Object.create(null)

      if (falseLargeScreen){
        member = this.videoOnMembers[0]
      } else if (trueLyLargeScreen){
        member = this.bigVideoUser
      } else {
        return false
      }
      // 主持人 slamMarkOn：万事贴 realtimeMarkOn：标注-激光笔 screenSharingOn：屏幕共享

      const isOpen = [this.otherScreenSharingOnUser?.id, this.realtimeMarkOnUser?.realtimeMarkTargetUserId, this.slamMarkOnUser?.slamMarkTargetUserId].includes(member.id)
      return (falseLargeScreen || trueLyLargeScreen) && !this.showLoading && !isOpen && this.localUser.isHost && !this.showFreeze && !this.showWhiteBoard
    }
  },

  watch: {
    '$store.state.status'(newValue, oldValue){
      if (oldValue === mqttcomm.MachineStatus.Rejoining && newValue === mqttcomm.MachineStatus.SessionEstablished) {
        // 页面刷新后进入会话
        this.initialization()
      }
    },

    // 当开视频的人数发生变动时重新计算布局
    videoOnMembers() {
      this.calcVideoMainSize()
    },

    // 监听当前是否有成员开启冻屏
    whiteBoardOnUser (newValue, oldValue) {
      if (newValue) {
        if (!oldValue) this.startWhiteBoard()
      } else {
        this.stopWhiteBoard()
        this.$store.commit('updateWhiteBoardLinesData')
      }
    },

    // 监听当前是否有成员开启冻屏
    freezeOnUser (newValue, oldValue) {
      if (newValue) {
        if (!oldValue) this.startFreeze()
      } else {
        this.stopFreeze()
      }
    },

    // 监听是否开启万事贴面板
    slamMarkOnUser (newValue, oldValue) {
      if (newValue) {
        if (!oldValue) this.startSlamMark()
      } else {
        this.stopSlamMark()
      }
    },

    // 监听当前是否有成员开启激光笔
    realtimeMarkOnUser (newValue, oldValue) {
      if (newValue) {
        if (!oldValue) this.startRealtimeMark()
      } else {
        this.stopRealtimeMark()
      }
    },

    // 在标注0.5s后清除标注数据以清除dom
    realtimeMarkList (value) {
      if (value && value.length > 0) {
        if (!this.debouncedClearPoint) {
          this.debouncedClearPoint = debounce(() => {
            this.$store.commit('clearRealtimeMark')
          }, 500)
        }
        this.debouncedClearPoint()
      }
    },

    // 监听是否有标注权限
    'localUser.isAnnotator' (newValue, oldValue) {
      if (newValue) {
        if (!oldValue) this.$message.success(this.$t('calling.grantedAnnotationPermissionOfMe'))
      } else if (!this.localUser.isHost && this.$store.state.status === mqttcomm.MachineStatus.SessionEstablished && oldValue) {
        this.$message.warn(this.$t('calling.revokedAnnotationPermission'))
      }
    },

    // 监听是否有主持人权限
    'localUser.isHost' (newValue, oldValue) {
      if (newValue) {
        if (!oldValue) this.$message.info(this.$t('calling.designatedHostOfMe'))
      }
    },

    // 监听申请的权限是否被拒绝
    '$store.state.roleApplicationRejected' (newValue, oldValue) {
      if (newValue && !oldValue && this.$store.state.status === mqttcomm.MachineStatus.SessionEstablished) {
        this.$message.warn(this.$t('calling.rejectsAnnotationPermission'))
        this.$store.commit('setRoleApplicationRejected', false)
      }
    },

    // 开启万事贴时关闭成员列表
    showSlamMark (value) {
      if (value) {
        this.show_members = false
        this.show_files = false
      }
    },

    // 开启白板是关闭成员和文件列表
    showWhiteBoard (value) {
      if (value) {
        this.show_members = false
        this.show_files = false
      }
    },
    // 开启冻屏时关闭成员列表
    showFreeze (value) {
      if (value) {
        this.show_members = false
        this.show_files = false
      }
    },

    // 开启激光笔时关闭成员列表
    showRealtimeMark (value) {
      if (value) {
        this.show_members = false
        this.show_files = false
      }
    },

    // 监听是否有不支持的功能开启
    '$store.state.showUnsupportedNotify' (newValue) {
      if (newValue) {
        // 弹出提示
        this.showUnsupportedNotify()
        this.$store.commit('resetShowUnsupportedNotify')
      }
    },

    // 当屏幕共享开启成功时，监听用户手动关闭共享的事件
    localScreenSharingOn (newValue, oldValue) {
      if (newValue && !oldValue) {
        this.localUser.rtcUser.videoTrack && this.localUser.rtcUser.videoTrack.on('track-ended', () => {
          this.toggleScreenSharing()
        })
      }
    }
  },

  beforeRouteLeave (to, from, next) {
    // 如果是挂断或者房间信息已丢失，则允许离开页面
    if (this.leaveFromHangup || !this.$store.state.currentSession?.roomId) {
      next()
    } else {
      // 拦截浏览器返回
      next(false)
      // 弹出是否确认离开的弹窗
      this.beforeHangUp()
    }
  },

  created() {
    this.resetNotReadFileCount()
    microPhoneAuthCheck(false)
    this.initMemberAvatar()
    // 更新协作详情,附件使用
    this.$store.dispatch('coc/viewCocDetail', this.conferenceIdOnRouter)
  },

  mounted() {
    loadingAnimation = lottie.loadAnimation({
      container: document.querySelector('.loading-animation'),
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: './lottie/talk-loading.json'
    })
    this.observeVideoContainer()
    if (this.$store.state.status === mqttcomm.MachineStatus.SessionEstablished) {
      // 正常进入会话
      this.initialization()
    }
    this.initMemberTip()
  },

  beforeDestroy() {
    this.stopDurationInterval()
    this.unobserveVideoContainer()
    this.cancelCheckVolume()
    this.resetLocalScreenSharing()
  },

  methods: {
    ...mapMutations('coc', ['resetNotReadFileCount']),
    initialization(){
      this.$store.dispatch('joinRtcRoom').then(() => {
        console.log('我进房间了')
        loadingAnimation.destroy()
        this.showLoading = false
        this.initLocalMedia()
        if (this.freezeOnUser) this.startFreeze()
        if (this.whiteBoardOnUser) this.startWhiteBoard()
        if (this.realtimeMarkOnUser) this.startRealtimeMark()
        if (this.slamMarkOnUser) this.startSlamMark()
      }).catch(err => {
        console.error('进入RTC房间异常', err)
        Modal.warning({
          title: this.$t('calling.RTCRoomAbnormal'),
          content: this.$t('calling.RefreshPage')
        })
      })
      this.startDurationInterval()
      this.calcVideoMainSize()
    },

    // 监听视频容器的大小变化
    observeVideoContainer () {
      this.resizeHandler = Global.throttle(this.calcVideoMainSize, 16)
      videoContainerResizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          videoMainElWidth = entry.contentRect.width
          videoMainElHeight = entry.contentRect.height
          this.resizeHandler()
        }
      })
      videoContainerResizeObserver.observe(document.querySelector('.page-talking main'))
    },

    unobserveVideoContainer () {
      videoContainerResizeObserver && videoContainerResizeObserver.disconnect()
    },

    // 通话时间计时
    startDurationInterval() {
      this.getDurationCache()
      this.stopDurationInterval()
      this.talkingDurationInterval = setInterval(this.addDuration, 1000)
    },

    stopDurationInterval() {
      this.talkingDurationInterval && clearInterval(this.talkingDurationInterval)
      this.talkingDurationInterval = undefined
    },

    addDuration() {
      let [hour, minute, second] = this.talkingDuration.split(':').map(Number)
      if (second === 59) {
        second = 0
        if (minute === 59) {
          minute = 0
          hour += 1
        } else {
          minute += 1
        }
      } else {
        second += 1
      }
      this.talkingDuration = [hour, minute, second].map(v => String(v).padStart(2, '0')).join(':')
      this.setDurationCache()
    },

    // 挂断前的一些判断与提示
    beforeHangUp () {
      // 本地视频源如果被占用则屏蔽挂断操作
      if (this.checkLocalVideoOccupied()) return
      if (this.localUser.isHost) {
        // 本地是主持人
        this.hostLeaveWarningVisible = true
      } else {
        // 不是主持人
        this.leaveWarningVisible = true
      }
    },

    // 离开协作，房间仍然存在，其他成员依然在协作中
    hangUp() {
      this.$store.dispatch('getMetuTypeSetting').then(microphoneClose => {
        this.leaveFromHangup = true
        this.$store.dispatch('leaveCall', { microphoneClose })
        this.$store.dispatch('leaveRtcRoom')
        this.clearMemberAvatarCache()
        this.clearDurationCache()
        this.$store.commit('clearWhiteBoardLinesData')
        this.$store.commit('removeWhiteBoardImage')
      })
    },

    // 终止协作，直接关闭房间，其他成员也都被踢出房间
    stopConference () {
      stopConference({
        conferenceId: this.conferenceIdOnRouter
      }).then(res => {
        if (res.data.code === 22013){
          if (this.$router.currentRoute.name === PAGE_NAME.Talking) {
            let sourceRoute = this.$router.currentRoute.query.from
            // 如果来源页面不是协作列表或者通讯录，则强制修改为协作列表，防止挂断通话跳转到异常页面
            if (![PAGE_NAME.Home, PAGE_NAME.Organ].includes(sourceRoute)) {
              sourceRoute = PAGE_NAME.Home
            }
            this.$store.commit('clearCurrentSession')
            this.$store.commit('clearWhiteBoardLinesData')
            this.$store.commit('removeWhiteBoardImage')
            this.$router.replace({ name: sourceRoute })
          }
        } else if (+res.data.code !== 0){
          res?.data?.msg && this.$message.error(res.data.msg)
        }
      })
    },

    initLocalMedia() {
      const user = this.localUser
      const controls = {}
      if (user.microphoneOn) controls.microphoneOn = true
      if (user.cameraOn) controls.cameraOn = true
      this.$store.dispatch('controlLocalMedia', controls)
      this.checkVolume()
    },

    changeCameraForce(type) {
      // 判断真大屏或假大屏
      const id = (!this.bigVideoUser) ? this.videoOnMembers[0].id : this.bigVideoUser.id
      if (type === 'amplify'){
        for (let i = 0; i < 5; i++){
          this.$store.dispatch('controlOtherMemberMedia', {
            userId: id,
            option: ESig.ControlOption.CAMERA_ZOOM,
            on: Global.DeviceStatus.on,
            uvalue: ++forceUvalue,
            fvalue: 0.1,
            type: 'force'
          })
        }
      } else if (type === 'reduce'){
        for (let i = 0; i < 5; i++){
          this.$store.dispatch('controlOtherMemberMedia', {
            userId: id,
            option: ESig.ControlOption.CAMERA_ZOOM,
            on: Global.DeviceStatus.on,
            uvalue: forceUvalue === 0 ? 0 : --forceUvalue,
            fvalue: -0.1,
            type: 'force'
          })
        }
      } else {
        this.visibleForce = false
      }
    },

    async toggleCamera() {
      // 检查摄像头权限
      if (!await this.checkCameraIsAvailable()) {
        return
      }
      // 本地视频源如果被占用则屏蔽切换摄像头的操作
      if (this.checkLocalVideoOccupied()) return
      // 如果屏幕分享在pending中， 直接屏蔽当前的操作
      if (this.localScreenSharingPending) return
      // 如果当前没有开启屏幕共享
      if (this.localScreenSharingOff) {
        // 如果当前是本地视频大屏，并且要关闭摄像头，要先退出大屏模式
        if (this.localCameraOn && this.bigVideoUser && this.localBigVideo) {
          this.stopBigVideo()
        }
        this.$store.dispatch('controlCamera', !this.localCameraOn)
      } else {
        this.screenCameraConflict = 'screen'
        this.conflictModalVisible = true
      }
    },
    // 先调用声网，在通过mqtt下发指令
    toggleMicrophoneBeforeMqtt: doubleCheck(function(){
      if (!microPhoneAuthCheck(true)) return false

      const changeMicrophoneState = !this.localMicrophoneOn
      rtc.toggleLocalAudio(
        this.sessionMediaType, // 在页面拿这个两个数值
        this.sessionMediaClient, // 在页面拿这个两个数值
        changeMicrophoneState,
        this.localUser.rtcUser
      ).then(_ => {
        this.$store.dispatch('controlMicrophone', changeMicrophoneState)
      }).catch((err) => {
        console.error('控制本地音频失败', err)
        if (changeMicrophoneState) {
          this.$message.warn(this.$t('calling.ObtainAudioPermissionsFailed'))
        } else {
          const modal = Modal.warning({
            okCancel: true,
            keyboard: false,
            okText: this.$t('main.Refresh'),
            cancelText: this.$t('common.Close'),
            title: this.$t('calling.MicrophoneAbnormal'),
            content: this.$t('calling.RefreshPage'),
            onOk: _ => {
              window.location.reload()
            },
            onCancel: _ => {
              modal.destroy()
            }
          })
        }
      })
    }, 500),
    toggleSpeaker() {
      this.$store.dispatch('controlSpeaker', !this.localSpeakerOn)
    },

    // 大屏和冻屏的自适应
    calcSingleVideoOrFreezeSize () {
      if (!videoMainElWidth) return
      const maxHeight = videoMainElHeight - 2
      const maxWidth = maxHeight / 0.5625

      if (videoMainElWidth < maxWidth + 2) {
        this.singleVideoOrFreezeStyle = {
          width: 'calc(100% - 2px)'
        }
      } else {
        this.singleVideoOrFreezeStyle = {
          width: maxWidth + 'px'
        }
      }
    },

    // 计算冻屏右侧操作栏的样式
    calcDrawOperationsStyle () {
      let height = videoMainElHeight - 48
      const maxHeight = 706
      const minHeight = 552
      const maxPadding = 40
      const minPadding = 16
      if (height < minHeight) height = minHeight
      if (height >= maxHeight) height = maxHeight
      const ratio = height / minHeight - 1
      const padding = (maxPadding - minPadding) * ratio + minPadding
      this.drawOperationsStyle = {
        height: height + 'px',
        padding: padding + 'px 0'
      }
    },

    // 不同数量的视频布局自适应
    calcVideoMainSize() {
      const mainWidth = videoMainElWidth - 2
      const mainHeight = videoMainElHeight - 2
      const length = this.videoOnMembers.length

      // 补丁
      this.calcSingleVideoOrFreezeSize()

      if (!mainWidth || length === 0) return
      let videoStyle = {}
      switch (true) {
        case (length === 1): {
          const maxHeight = mainHeight - 2
          const maxWidth = maxHeight / 0.5625 + 2
          if (mainWidth < maxWidth) {
            videoStyle = {
              width: 'calc(100% - 2px)'
            }
          } else {
            videoStyle = {
              width: (maxHeight - 2) / 0.5625 + 'px'
            }
          }
          this.matrix = 1
          break
        }
        case (length === 2): {
          document.querySelector('.video-container').style.width = '100%'
          videoStyle = {
            width: 'calc(50% - 2px)'
          }
          this.matrix = 2
          break
        }
        case (length <= 4): {
          const maxHeight = mainHeight - 4
          const maxWidth = maxHeight / 0.5625
          const vWrapWidth = maxWidth + 4
          if (mainWidth < vWrapWidth) {
            document.querySelector('.video-container').style.width = '100%'
            videoStyle = {
              width: 'calc(50% - 2px)'
            }
          } else {
            document.querySelector('.video-container').style.width = vWrapWidth + 'px'
            videoStyle = {
              width: maxWidth / 2 - 1 + 'px'
            }
          }
          this.matrix = 4
          break
        }
        case (length <= 6): {
          document.querySelector('.video-container').style.width = '100%'
          videoStyle = {
            width: 'calc(33.33333% - 2px)'
          }
          this.matrix = 6
          break
        }
        case (length <= 9): {
          const maxHeight = mainHeight - 6
          const maxWidth = maxHeight / 0.5625
          const vWapWidth = maxWidth + 6
          if (mainWidth < vWapWidth) {
            document.querySelector('.video-container').style.width = '100%'
            videoStyle = {
              width: 'calc(33.33333% - 2px)'
            }
          } else {
            document.querySelector('.video-container').style.width = vWapWidth + 'px'
            videoStyle = {
              width: maxWidth / 3 - 1 + 'px'
            }
          }
          this.matrix = 9
          break
        }
        case (length <= 12): {
          const maxHeight = mainHeight - 6
          const maxHeightItem = maxHeight / 3
          const maxWidthItem = maxHeightItem / 0.5625
          const vWrapWidth = maxWidthItem * 4 + 8
          if (mainWidth < vWrapWidth) {
            document.querySelector('.video-container').style.width = '100%'
            videoStyle = {
              width: 'calc(25% - 2px)'
            }
          } else {
            document.querySelector('.video-container').style.width = vWrapWidth + 'px'
            videoStyle = {
              width: maxWidthItem + 'px'
            }
          }
          this.matrix = 12
          break
        }
        case (length <= 16): {
          const maxHeight = mainHeight - 8
          const maxWidth = maxHeight / 0.5625
          if (mainWidth < maxWidth + 8) {
            document.querySelector('.video-container').style.width = '100%'
            videoStyle = {
              width: 'calc(25% - 4px)'
            }
          } else {
            document.querySelector('.video-container').style.width = maxWidth + 8 + 'px'
            videoStyle = {
              width: maxWidth / 4 - 1 + 'px'
            }
          }
          this.matrix = 16
          break
        }
      }
      this.videoPlayerStyle = videoStyle
      this.calcSingleVideoOrFreezeSize()
      this.calcDrawOperationsStyle()
    },

    // 放大视频
    playBigVideo(member, forceBig) {
      // 如果当前只有一个或者没有视频则不操作
      if (this.videoOnMembers?.length < 2 && !forceBig) return
      this.$store.dispatch('toggleBigVideo', {
        member,
        isPlay: true
      })
      const playDom = () => {
        this.calcSingleVideoOrFreezeSize()
        if (!this.localBigVideo || (this.localBigVideo && this.localCameraOn)) {
          if (this.bigVideoUser.rtcUser.videoTrack) {
            this.bigVideoUser.rtcUser.videoTrack.play('big-video-player', Global.AGORA_VIDEO_CONFIG)
            rtc.toggleRemoteVideoStreamType(this.sessionMediaType, this.sessionMediaClient, { bigVideoUser: this.bigVideoUser, bigVideo: true, sessionMembers: this.sessionMembers, isLocal: this.bigVideoUser.id === this.localUser.id })
          } else {
            setTimeout(playDom, 100)
          }
        }
      }
      this.$nextTick(playDom)
    },

    // 缩小视频
    stopBigVideo() {
      const bigVideoUser = this.mediaOnMembers.find(member => member.id === this.bigVideoUser.id)
      this.$store.dispatch('toggleBigVideo', {
        member: bigVideoUser,
        isPlay: false
      })
      this.$nextTick(() => {
        // 因为这个时候大屏的状态已经关闭，所以不能用this.localBigVideo来判断，要用前面保存的bigVideoUser来判断
        if (bigVideoUser.id !== this.localUser.id || (bigVideoUser.id === this.localUser.id && this.localCameraOn)) {
          bigVideoUser.rtcUser.videoTrack && bigVideoUser.rtcUser.videoTrack.play(`video-player-${bigVideoUser.id}`, Global.AGORA_VIDEO_CONFIG)
          rtc.toggleRemoteVideoStreamType(this.sessionMediaType, this.sessionMediaClient, { bigVideoUser, bigVideo: false, sessionMembers: this.sessionMembers, isLocal: bigVideoUser.id === this.localUser.id })
        }
      })
    },

    toggleFiles () {
      if (this.show_members) {
        this.show_members = false
      }
      this.resetNotReadFileCount()
      !this.showFreeze && !this.showRealtimeMark && !this.showSlamMark && (this.show_files = !this.show_files)
    },
    toggleMembers () {
      if (this.show_files) {
        this.show_files = false
      }
      // 因为冻屏和激光笔，右侧都有贴边的操作栏，一旦打开成员列表就会顶开操作栏，所以在冻屏和激光笔开启时不能打开成员列表
      !this.showFreeze && !this.showRealtimeMark && !this.showSlamMark && (this.show_members = !this.show_members)
    },

    checkRole () {
      // 检测本地用户是否有标注权限
      if (!this.localUser.isHost && !this.localUser.isAnnotator) {
        this.checkRoleModalVisible = true
        return false
      }
      return true
    },

    // 申请权限
    applyRole () {
      this.$store.dispatch('localUserApplyRole', [Global.MEMBER_ROLE.ROLE_SPECIALIST]).then(() => {
        this.checkRoleModalVisible = false
      })
    },

    // 授予他人权限
    grantRole () {
      const { roleName } = this.someoneApplyRole
      this.$store.dispatch('confirmOtherUserRole', Global.IS_GRANT_ROLE.allow).then(res => {
        if (+res.data.code === 0) {
          this.$message.success(`${this.$t('common.Grant')}${roleName}${this.$t('common.PermissionsSucc')}`)
        } else {
          this.$message.success(`${this.$t('common.Grant')}${roleName}${this.$t('common.PermissionsFail')}`)
        }
      })
    },

    // 拒绝授予他人权限
    rejectApplyRole () {
      this.$store.dispatch('confirmOtherUserRole', Global.IS_GRANT_ROLE.disallow)
    },

    // 从视频截图转成文件
    imageToFile (image) {
      return new Promise(resolve => {
        const canvas = document.createElement('canvas')
        canvas.width = image.width
        canvas.height = image.height
        const context = canvas.getContext('2d')
        context.putImageData(image, 0, 0)
        canvas.toBlob(blob => {
          const file = new File([blob], 'base_map.jpg')
          resolve(file)
        }, 'image/jpeg')
      })
    },

    // 上传视频截图
    uploadImageFile (file) {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('channelId', this.$store.state.currentSession.roomId)
      return uploadFile(formData)
    },

    toggleWhiteBoard () {
      // 已存在冲突的功能在使用中
      if (this.checkConflictFunction('whiteBoard')) return

      // 当前无人冻屏，本地用户需要主动发起冻屏，则需要检测权限
      if (!this.whiteBoardOnUser && !this.checkRole()) return

      // 当前没有人在冻屏，用户要自己发起冻屏
      if (!this.whiteBoardOnUser) {
        this.launchWhiteBoard()
      } else {
        // 当前是用户自己的冻屏
        if (this.localWhiteBoardOn) {
          // 正在冻屏中，关闭
          if (this.showWhiteBoard) {
            this.closeWhiteBoard()
          }
        } else {
          // 不是自己冻屏
          this.showWhiteBoard = !this.showWhiteBoard
        }
      }
    },

    toggleFreeze: doubleCheck(function(){
      // 已存在冲突的功能在使用中
      if (this.checkConflictFunction('freeze')) return

      // 当前无人冻屏，本地用户需要主动发起冻屏，则需要检测权限
      if (!this.freezeOnUser && !this.checkRole()) return

      // 当前没有人在冻屏，用户要自己发起冻屏
      if (!this.freezeOnUser) {
        this.launchFreeze()
      } else {
        // 当前是用户自己的冻屏
        if (this.localFreezeOn) {
          // 正在冻屏中，关闭
          if (this.showFreeze) {
            this.closeFreeze()
          }
        } else {
          // 不是自己冻屏
          this.showFreeze = !this.showFreeze
        }
      }
    }, 500),

    // 主动发起白板操作
    async launchWhiteBoard () {
      this.$store.dispatch('toggleLocalUserWhiteBoard', {
        on: true,
        color: Global.WHITEBOARDCOLOR
      })
      // this.$store.commit('setLinesData', {})
    },

    // 主动发起冻屏请求
    async launchFreeze () {
      let videoTrack
      // 开启了大屏，则冻结大屏
      if (this.bigVideoUser) {
        videoTrack = this.bigVideoUser.rtcUser.videoTrack
      } else {
        // 没有开启大屏，而且还有多个视频，不予开启冻屏
        if (this.videoOnMembers && this.videoOnMembers.length > 1) return
        // 当前只有一个视频，则冻结此视频
        videoTrack = this.videoOnMembers[0]?.rtcUser?.videoTrack
      }
      if (!videoTrack) return
      const imageData = videoTrack?.getCurrentFrameData()
      const file = await this.imageToFile(imageData)
      const imageUrl = Global.resolvePath(await this.uploadImageFile(file))
      this.uploadImageOfFreezeScreenFetch(imageUrl, 0)
      this.$store.dispatch('toggleLocalUserFreeze', {
        on: true,
        imageUrl,
        imageData
      })
    },

    // 上传冻屏图片
    async uploadImageOfFreezeScreenFetch (url, frozenType) {
      try {
        const name = `freeze_${frozenType ? 'stop' : 'start'}_${+new Date()}.jpg`
        const d = {
          conferenceId: this.conferenceIdOnRouter,
          files: [{ url, name, type: 1, frozenType }],
          notify: false
        }
        await addAttachFiles(d)
      } catch (error) {
        console.error('上传冻屏图片错误: ' + error.message)
      }
    },
    // 主动关闭白板请求
    closeWhiteBoard () {
      this.$store.dispatch('toggleLocalUserWhiteBoard', {
        on: false
      })
    },

    // 主动关闭冻屏请求
    closeFreeze () {
      this.$store.dispatch('toggleLocalUserFreeze', {
        on: false
      })
    },

    // 开启白板
    startWhiteBoard () {
      this.showWhiteBoard = true
    },

    // 关闭白板
    async stopWhiteBoard () {
      this.showWhiteBoard = false
      this.$refs.drawInWhiteBoard.stopDraw()
    },

    // 开启冻屏
    startFreeze () {
      preFreezeUserId = this.freezeOnUser.id

      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.src = this.freezeOnUser.freezeImage
      img.addEventListener('load', () => {
        this.showFreeze = true
        this.freezeBaseMap = img
        this.$refs.drawInFreeze.startDraw()
      })
    },

    // 关闭冻屏
    async stopFreeze () {
      const file = this.$refs.drawInFreeze.getImageFileOfCanvas()
      const url = Global.resolvePath(await this.uploadImageFile(file))
      this.localUser.id === preFreezeUserId && this.uploadImageOfFreezeScreenFetch(url, 1)

      this.showFreeze = false
      this.freezeBaseMap = undefined
      this.$store.commit('clearLinesData')
      this.$refs.drawInFreeze.stopDraw()
    },

    localDrawLine (line, type) {
      this.$store.dispatch('localUserDrawLine', line)

      const params = {
        ...line,
        colour: line.color,
        controluserid: this.localUser.id
      }

      if (type === 'freeze') {
        this.$store.commit('addLinesData', params)
      } else if (type === 'whiteBoard') {
        this.$store.commit('addWhiteBoardLinesData', params)
      }
    },

    localStickerDraw (file) {
      this.$store.dispatch('setWhiteBoardImage', file.url)
      const params = {
        content: { type: 'IMAGE_MSG', text: file.url, filename: file.name, filesize: file.size },
        conferenceId: this.conferenceIdOnRouter
      }
      this.$store.dispatch('localUserSticker', params)
    },

    localUndoDraw (type) {
      this.$store.dispatch('localUserUndoDraw')

      if (type === 'freeze') {
        this.$store.commit('deleteLinesData', this.localUser.id)
      } else if (type === 'whiteBoard') {
        this.$store.commit('deleteWhiteBoardLinesData', this.localUser.id)
      }
    },

    localClearDraw (type) {
      this.$store.dispatch('localUserClearDraw')

      if (type === 'freeze') {
        this.$store.commit('clearLinesData')
      } else if (type === 'whiteBoard') {
        this.$store.commit('clearWhiteBoardLinesData')
        this.$store.commit('removeWhiteBoardImage')
      }
    },

    toggleRealtimeMark () {
      // 已存在冲突的功能在使用中
      if (this.checkConflictFunction('realtimeMark')) return

      // 当前无人激光笔，本地用户需要主动发起激光笔，则需要检测权限
      if (!this.realtimeMarkOnUser && !this.checkRole()) return

      // 清除标注点
      this.$store.commit('clearRealtimeMark')

      // 当前没有人在激光笔，用户要自己发起激光笔
      if (!this.realtimeMarkOnUser) {
        this.launchRealtimeMark()
      } else {
        // 当前是用户自己的激光笔
        if (this.localRealtimeMarkOn) {
          // 正在激光笔中，关闭
          if (this.showRealtimeMark) {
            this.closeRealtimeMark()
          }
        } else {
          // 不是自己激光笔
          if (this.showRealtimeMark) {
            this.stopRealtimeMark()
          } else {
            this.startRealtimeMark()
          }
        }
      }
    },

    // 主动发起激光笔请求
    async launchRealtimeMark () {
      // 开启激光笔时选中的视频用户
      let targetUser
      if (this.bigVideoUser) {
        targetUser = this.bigVideoUser // 大屏用户
      } else if (this.videoOnMembers.length === 1) {
        targetUser = this.videoOnMembers[0] // 只有一个视频源，则默认为该视频源用户
      } else {
        return // 不是以上两种情况，不允许主动开启
      }
      this.$store.dispatch('toggleLocalUserRealtimeMark', {
        on: true,
        userId: targetUser.memberId
      })
    },

    // 主动关闭激光笔请求
    closeRealtimeMark () {
      this.$store.dispatch('toggleLocalUserRealtimeMark', {
        on: false
      })
    },

    // 开启激光笔
    startRealtimeMark () {
      const targetUser = this.mediaOnMembers.find(member => +member.id === +this.realtimeMarkOnUser.realtimeMarkTargetUserId)
      if (!this.bigVideoUser) {
        this.playBigVideo(targetUser, true)
      } else if (this.bigVideoUser.id !== targetUser.id) {
        this.stopBigVideo()
        this.playBigVideo(targetUser, true)
      }
      this.showRealtimeMark = true
    },

    // 关闭激光笔
    stopRealtimeMark () {
      this.showRealtimeMark = false
      if (this.bigVideoUser) this.stopBigVideo()
    },

    // 激光笔进行标注
    drawLocalRealtimeMark (point) {
      this.$store.commit('addRealtimeMark', {
        posx: point.posX,
        posy: point.posY,
        colour: point.color
      })
      this.$store.dispatch('localUserRealtimeMark', point)
    },

    // 实时检测麦克风音量
    checkVolume () {
      for (const member in this.audioVolume) {
        this.audioVolume[member] = 14
      }
      this.microphoneOnMember.forEach(member => {
        const audioTrack = member.rtcUser.audioTrack
        if (audioTrack) {
          const volume = audioTrack.getVolumeLevel() || 0
          this.audioVolume[member.id] = 14 * (1 - volume)
        }
      })
      checkVolumeAnimation = requestAnimationFrame(this.checkVolume)
    },

    // 取消检测麦克风音量
    cancelCheckVolume () {
      cancelAnimationFrame(checkVolumeAnimation)
    },

    // 不支持的功能给出提示
    showUnsupportedNotify () {
      const msgKey = 'unsupported'
      this.$message.warn({
        content: h => {
          return h('div', {
            style: {
              display: 'inline-block'
            }
          }, [
            this.$t('calling.WebNotSupportFunction'),
            h('span', {
              style: {
                color: '#236CFF',
                textDecoration: 'underline',
                cursor: 'pointer'
              },
              on: {
                click: () => {
                  window.open('https://aryun.com/product/hileia')
                }
              }
            },
            [
              this.$t('calling.DownloadWinClient')
            ]),
            this.$t('calling.Use')
          ])
        },
        key: msgKey
      })
    },

    // 成员按钮上的提示的容器
    memberTipContainer (node) {
      return node.parentNode
    },

    // 展示并定时关闭成员按钮上的小提示
    initMemberTip () {
      setTimeout(() => {
        this.showMemberTip = true
      }, 300)
      setTimeout(() => {
        this.showMemberTip = false
      }, 5 * 1000)
    },

    // 初始化用户头像信息
    initMemberAvatar () {
      const avatarCache = this.$ss.get(CACHE_KEY.MEMBER_AVATAR) || {}
      if (avatarCache[this.conferenceIdOnRouter]) {
        this.avatarMap = avatarCache[this.conferenceIdOnRouter]
      } else {
        generateAvatarMap(this.$store.state.collaborator.treeData, this.avatarMap)
        this.avatarMap = { ...this.avatarMap }
        this.$ss.set(CACHE_KEY.MEMBER_AVATAR, {
          [this.conferenceIdOnRouter]: this.avatarMap
        })
      }
    },

    // 清除本次本次会话的头像缓存
    clearMemberAvatarCache () {
      this.$ss.remove(CACHE_KEY.MEMBER_AVATAR)
    },

    // 缓存当前通话时间
    setDurationCache () {
      this.$ss.set(CACHE_KEY.TALKING_DURATION, {
        [this.conferenceIdOnRouter]: this.talkingDuration
      })
    },

    // 获取缓存中的通话时间
    getDurationCache () {
      const duration = this.$ss.get(CACHE_KEY.TALKING_DURATION) || {}
      if (duration[this.conferenceIdOnRouter]) {
        this.talkingDuration = duration[this.conferenceIdOnRouter]
      }
    },

    // 清除缓存的通话时间
    clearDurationCache () {
      this.$ss.remove(CACHE_KEY.TALKING_DURATION)
    },
    // 主动发起万事贴
    async launchSlamMark () {
      // 获取当前视频用户
      let targetUser
      if (this.bigVideoUser) {
        targetUser = this.bigVideoUser // 大屏用户
      } else if (this.videoOnMembers.length === 1) {
        targetUser = this.videoOnMembers[0] // 只有一个视频源，则默认为该视频源用户
      } else {
        return // 不是以上两种情况，不允许主动开启
      }

      // 判断视频是否是移动端视频 rVD = remoteVideoDevice
      const rVD = targetUser.deviceType
      if (!isMobileClient(rVD)){
        this.$message.warn(this.$t('calling.OtherNotSupportFunction'))
        return
      }

      let videoTrack
      // 开启了大屏，则冻结大屏
      if (this.bigVideoUser) {
        videoTrack = this.bigVideoUser.rtcUser.videoTrack
      } else {
        // 没有开启大屏，而且还有多个视频，不予开启冻屏
        if (this.videoOnMembers && this.videoOnMembers.length > 1) return
        // 当前只有一个视频，则冻结此视频
        videoTrack = this.videoOnMembers[0]?.rtcUser?.videoTrack
      }
      if (!videoTrack) return

      this.$store.dispatch('toggleSlamMark', {
        on: true,
        userId: targetUser.memberId // remote phone user
      })
    },
    // 主动关闭冻屏请求
    closeSlamMark () {
      this.$store.dispatch('toggleSlamMark', {
        on: false
      })
    },

    // 万事贴初次点击
    toggleSlamMark () {
      // 检查冲突功能
      if (this.checkConflictFunction('slamMark')) return

      // 检查权限
      if (!this.slamMarkOnUser && !this.checkRole()) return

      if (!this.slamMarkOnUser) {
        // 启动万事贴
        this.launchSlamMark()
      } else {
        // 当前是用户自己的万事贴
        if (this.localSlamMarkOn) {
          // 正在万事贴中，关闭
          if (this.showSlamMark) {
            this.closeSlamMark()
          }
        } else {
          // 不是自己万事贴
          if (this.showSlamMark) {
            this.stopSlamMark()
          } else {
            this.startSlamMark()
          }
        }
      }
    },
    // 万事贴撤销
    localUndoSlamMark () {
      // 和冻屏相同的撤销数值
      this.$store.dispatch('localUserUndoDraw')
    },

    // 万事贴清空
    localClearSlamMark () {
      // 和冻屏相同的撤销数值
      this.$store.dispatch('localUserClearDraw')
    },
    // 开始万事贴
    startSlamMark () {
      const targetUser = this.mediaOnMembers.find(member => +member.id === +this.slamMarkOnUser.slamMarkTargetUserId)
      if (!this.bigVideoUser) {
        this.playBigVideo(targetUser, true)
      } else if (this.bigVideoUser.id !== targetUser.id) {
        this.stopBigVideo()
        this.playBigVideo(targetUser, true)
      }
      this.showSlamMark = true
    },
    // 万事贴标注
    drawLocalSlamMark (point) {
      this.$store.dispatch('localUserSlamMark', point)
    },

    // 关闭万事贴
    stopSlamMark () {
      this.showSlamMark = false
      if (this.bigVideoUser) this.stopBigVideo()
    },

    // 点击屏幕共享按钮
    toggleScreenSharing () {
      // 如果有其他人在屏幕共享则屏蔽操作
      if (this.otherScreenSharingOnUser) return
      // 已存在冲突的功能在使用中
      if (this.checkConflictFunction('screen')) return
      // 如果屏幕分享在pending中， 说明上次的操作还没结束，直接屏蔽当前的操作
      if (this.localScreenSharingPending) return
      // 如果当前没有开启摄像头
      if (!this.localCameraOn) {
        if (this.localScreenSharingOn) {
          this.closeScreenSharing()
        } else {
          this.fakeOpenScreenSharing()
        }
      } else {
        this.screenCameraConflict = 'camera'
        this.conflictModalVisible = true
      }
    },

    // 开启屏幕共享
    fakeOpenScreenSharing () {
      this.$store.dispatch('fakeControlScreenSharing', true)
      // 屏幕共享状态置为pending
      this.$store.commit('setLocalScreenSharingStatus', Global.SCREEN_SHARING_STATUS.pending)
    },

    // 关闭屏幕共享
    closeScreenSharing () {
      this.$store.dispatch('fakeControlScreenSharing', false)
      // 屏幕共享状态置为pending
      this.$store.commit('setLocalScreenSharingStatus', Global.SCREEN_SHARING_STATUS.pending)
    },

    // 在二次确认弹窗中确定开启屏幕共享操作
    confirmScreenWarning () {
      // 如果是从摄像头切换成屏幕共享
      if (this.screenCameraConflict) {
        // 已经开了摄像头，需要关闭摄像头，开启屏幕共享
        this.$store.dispatch('switchCameraToScreen')
        this.resetScreenCameraConflict()
      } else {
        // 没有冲突，直接开启屏幕共享
        this.$store.dispatch('controlLocalScreenSharing', true)
      }
      this.$store.commit('setLocalScreenSharingWarningVisible', false)
    },

    cancelScreenWarning () {
      if (this.screenCameraConflict) {
        this.$store.commit('setLocalScreenSharingStatus', Global.SCREEN_SHARING_STATUS.off)
        this.resetScreenCameraConflict()
      } else {
        this.closeScreenSharing()
      }
      this.$store.commit('setLocalScreenSharingWarningVisible', false)
    },

    // 设定弹窗的父容器为本页面
    modalContainer () {
      return document.querySelector('.page-talking')
    },

    // 重置屏幕共享和摄像头的冲突
    resetScreenCameraConflict () {
      this.screenCameraConflict = false
      this.conflictModalVisible = false
    },

    // 解决屏幕共享和摄像头的冲突
    resolveScreenCameraConflict () {
      // 如果当前是本地视频大屏，要先退出大屏模式
      if (this.localBigVideo) {
        this.stopBigVideo()
      }
      if (this.screenCameraConflict === 'camera') {
        this.conflictModalVisible = false
        this.$store.commit('setLocalScreenSharingWarningVisible', true)
      } else if (this.screenCameraConflict === 'screen') {
        // 已经开了屏幕共享，需要关闭屏幕共享，开启摄像头
        this.$store.dispatch('switchScreenToCamera')
        this.resetScreenCameraConflict()
      }
    },

    // 重置本地屏幕分享的状态为关闭态
    resetLocalScreenSharing () {
      this.$store.commit('setLocalScreenSharingStatus', Global.SCREEN_SHARING_STATUS.off)
    },

    // 检测冲突的功能并提示
    checkConflictFunction (fnc) {
      let conflict
      switch (fnc) {
        case 'screen':
          conflict = !!this.freezeOnUser || !!this.realtimeMarkOnUser || !!this.slamMarkOnUser
          break
        case 'whiteBoard':
          conflict = !!this.freezeOnUser || !this.localScreenSharingOff || !!this.otherScreenSharingOnUser || !!this.realtimeMarkOnUser || !!this.slamMarkOnUser
          break
        case 'freeze':
          conflict = !this.localScreenSharingOff || !!this.otherScreenSharingOnUser || !!this.realtimeMarkOnUser || !!this.slamMarkOnUser
          break
        case 'realtimeMark':
          conflict = !!this.freezeOnUser || !this.localScreenSharingOff || !!this.otherScreenSharingOnUser || !!this.slamMarkOnUser
          break
        case 'slamMark':
          conflict = !!this.freezeOnUser || !this.localScreenSharingOff || !!this.otherScreenSharingOnUser || !!this.realtimeMarkOnUser
          break
        default:
          conflict = false
          break
      }
      if (conflict) {
        this.$message.warn({
          content: this.$t('calling.MoreFunctionNotUseOfSameTime'),
          key: 'conflict-function'
        })
      }
      return conflict
    },

    // 检测当前视频源是否被激光笔功能（当前用户开启或其他用户使用此视频源开启）占用
    checkLocalVideoOccupied () {
      const msgKey = 'check-local-video-occupied'
      if (this.localVideoBeUsedToRealtimeMark) {
        this.$message.warn({
          content: this.$t('calling.LaserPointerUsing'),
          key: msgKey
        })
        return true
      }
    },

    // 检测摄像头是否可使用,使用声网检测失败，需下面函数自定义提前检测
    // async checkCameraIsAvailable() {
    //   return await AgoraRTC.getCameras().then(res => { return true }).catch(err => {
    //     console.log('检测摄像头：', err.message)
    //     this.$message.warn('获取摄像头权限失败，请允许网站获取摄像头权限！')
    //     return false
    //   })
    // },
    async checkCameraIsAvailable() {
      return new Promise((resolve, reject) => {
        function getUserMedia(video, success, error) {
          if (window.navigator.mediaDevices.getUserMedia) {
            // 最新的标准API
            window.navigator.mediaDevices.getUserMedia(video).then(success).catch(error)
          } else if (window.navigator.webkitGetUserMedia) {
            // webkit核心浏览器
            window.navigator.webkitGetUserMedia(video, success, error)
          } else if (window.navigator.mozGetUserMedia) {
            // firfox浏览器
            window.navigator.mozGetUserMedia(video, success, error)
          } else if (window.navigator.getUserMedia) {
            // 旧版API
            window.navigator.getUserMedia(video, success, error)
          } else {
            console.error('不支持访问用户媒体！')
          }
        }
        function success(stream) {
          window.mediaStreamTrack = typeof stream.stop === 'function' ? stream : stream.getTracks()[0]
          window.mediaStreamTrack && window.mediaStreamTrack.stop()
          delete window.mediaStreamTrack
          resolve(true)
        }
        function error(err) {
          console.error(`访问用户媒体设备失败${err.name}, ${err.message}`)
          reject(err)
        }
        getUserMedia({ video: true, audio: false }, success, error)
      }).catch(err => {
        console.log('检测摄像头：', err.message)
        this.$message.warn(this.$t('calling.ObtainCameraPermissionsFailed'))
        return false
      })
    },
    // 焦点控制支持的设备类型
    isForceMobileClient (deviceType) {
      return deviceType === EDeviceType.IOS ||
        deviceType === EDeviceType.ANDROID ||
        deviceType === EDeviceType.GLASSES ||
        deviceType === EDeviceType.H100 ||
        deviceType === EDeviceType.CIMO ||
        deviceType === EDeviceType.WU_KONG
    }
  }
}
</script>

<style lang="scss" scoped>
.page-talking {
  width: 100%;
  height: 100%;
  min-width: 1200px;
  min-height: 640px;
  background-color: #F7F8FA;
  box-shadow: inset 5px 0 10px 0 rgba(151, 171, 199, 0.06);
  display: flex;
  flex-flow: column nowrap;
  position: relative;

  header {
    flex: 0 0 50px;
    width: 100%;
    height: 50px;
    background-color: #fff;
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;

    h1,
    .duration {
      font-size: 18px;
      color: #000000;
      margin: 0;
    }

    h1 {
      margin-right: 24px;
      font-weight: 500;
    }
    .signal{
      margin-left: 10px;
    }
  }

  .force-control{
    position: absolute;
    left: 0;
    top:50%;
    transform: translateY(-50%);
    z-index: 5;
    width: 40px;
    height: 78px;
    background: rgba(10,15,26,0.2);
    border-radius: 1px 4px 4px 1px;

    //display: flex;
    ////justify-content: center;
    //flex-direction: column;
    //align-items: center;
    .container{
      position: relative;
      height: inherit;
      width: inherit;
      .reduce{
        position:absolute;
        width:13px;
        top: 12px;
        left: 12px;
        content: url('~@/assets/talking/force-reduce.png');
        &:hover{
          cursor: pointer;
          transform: scale(1.2);
          content: url('~@/assets/talking/force-reduce-hover.png');
        }
      }
      .amplify{
        position:absolute;
        width:13px;
        top: 47px;
        left: 12px;
        content: url('~@/assets/talking/force-amplify.png');
        &:hover{
          transform: scale(1.2);
          cursor: pointer;
          content: url('~@/assets/talking/force-amplify-hover.png');
        }
      }
    }
  }

  main {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;

    .loading-rtc {
      position: absolute;
      text-align: center;

      .loading-animation {
        width: 199px;
        height: 198px;
      }

      > span {
        font-size: 16px;
        color: #999B9F;
        line-height: 22px;
      }
    }

    .self-avatar-container {
      width: 88px;
      height: 88px;
      border-radius: 16px;
      overflow: hidden;
      position: absolute;
      display: flex;
      justify-content: center;

      > img {
        width: 100%;
        height: 100%;
      }

      .user-tips {
        width: 80px;
        padding: 0 8px;
        left: unset;
        bottom: 4px;
        border-radius: 12px;

        .volume-off {
          position: absolute;
          left: 5px;
        }

        .name {
          position: absolute;
          left: 23px;
        }
      }
    }

    .video-player {
      position: relative;
      background-color: rgba(190, 199, 217, 0.2);
      overflow: hidden;
      user-select: none;

      &::before {
        display: block;
        width: 100%;
        padding-top: 56.25%;
        content: '';
      }

      ::v-deep > div[id^=agora-video] {
        position: absolute !important;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
      }

      ::v-deep > video {
        position: absolute !important;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
      }
    }

    .screen-sharing-tip {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      border: 3px solid #60CE64;
      font-size: 32px;
      color: #999B9F;
      z-index: 1;
      background-color: rgb(236,238,243);
      > span {
        font-size: 16px;
        font-weight: 500;
        line-height: 22px;
      }
    }

    .user-tips {
      height: 24px;
      position: absolute;
      left: 8px;
      bottom: 8px;
      padding: 0 8px 0 5px;
      background-color: rgba(255, 255, 255, 0.9);
      border-radius: 8px;
      display: flex;
      flex-flow: row nowrap;
      justify-content: center;
      align-items: center;
      z-index: 2;

      .volume-off {
        display: block;
        width: 14px;
        height: 14px;
        margin-right: 4px;
        background-size: 100% 100%;
        background-image: url('~@/assets/talking/volume-off.png');;
        background-repeat: no-repeat;
      }

      .volume {
        display: block;
        width: 14px;
        height: 14px;
        position: absolute;
        top: 5px;
        left: 5px;
        background-size: 14px 14px;
        background-image: linear-gradient(#2ACA41, #2ACA41);
        background-repeat: no-repeat;
        background-position-y: 14px;
        mask-image: url('~@/assets/talking/volume-off.png');
      }

      .name {
        font-size: 12px;
        font-weight: 500;
        color: #000000;
        cursor: default;
      }

      &.force{
        left: 8px;
        top: 8px
      }
    }

    .video-container {
      display: flex;
      flex-flow: row wrap;
      justify-content: left;
      padding-top: 2px;
      padding-left: 2px;
      &.have-1-video {
        width: 100%;
        justify-content: center;
      }

      &.have-2-video {
        width: 100%;

        > div[id^=video-player] {

          &:first-child {
            margin-right: 2px;
          }
        }
      }

      &.have-4-video {
        > div[id^=video-player] {

          &:first-child {
            margin-right: 2px;
            margin-bottom: 2px;
          }

          &:nth-child(2) {
            margin-bottom: 2px;
          }

          &:nth-child(3) {
            margin-right: 2px;
          }
        }
      }

      &.have-6-video {
        > div[id^=video-player] {
          margin-right: 2px;
          margin-bottom: 2px;

          &:nth-child(3n) {
            margin-right: 0;
          }
        }
      }
      &.have-9-video {
        > div[id^=video-player] {
          margin-right: 2px;
          margin-bottom: 2px;

          &:nth-child(3n) {
            margin-right: 0;
          }

        }
      }
      &.have-12-video {
        > div[id^=video-player] {
          margin-right: 2px;
          margin-bottom: 2px;

          &:nth-child(4n) {
            margin-right: 0;
          }

        }
      }

      &.have-16-video {
        > div[id^=video-player] {
          margin-right: 2px;
          margin-bottom: 2px;

          &:nth-child(4n) {
            margin-right: 0;
          }

          &:nth-child(n + 13) {
            margin-bottom: 0;
          }
        }
      }

    }

    .big-video-container {
      width: 100%;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      z-index: 3;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: #F7F8FA;
      // background-color: #202227;
    }

    .whiteboard-container,.freeze-container, .realtime-mark-container,.slam-mark-container {
      z-index: 4;
    }
  }

  footer {
    flex: 0 0 128px;
    width: 100%;
    height: 128px;
    padding-top: 20px;
    background-color: #fff;
    position: relative;

    .operations {
      display: flex;
      flex-flow: row nowrap;
      justify-content: center;
      list-style: none;

      li:nth-child(3),
      li:nth-child(10){
        margin-right: 90px;
      }
      li:last-child {
        margin-right: 0;
      }
    }

    .operation-btn {
      // width: 56px;
      margin-right: 40px;
      text-align: center;
      user-select: none;

      ::v-deep .member-tip {
        .ant-tooltip-arrow::before {
          background-color: #fff;
        }

        .ant-tooltip-inner {
          padding: 14px 16px;
          background-color: #fff;
          font-size: 14px;
          font-weight: 400;
          color: #000000;
          line-height: 20px;
        }
      }

      .icon-wrapper {
        width: 56px;
        height: 56px;
        margin-bottom: 8px;
        padding: 0;
        background-color: #FFFFFF;
        border: 1px solid #EDEFF2;
        border-radius: 50%;
        box-shadow: 0 4px 6px 0 rgba(89, 132, 219, 0.08);
        cursor: pointer;
        position: relative;
        overflow: hidden;
        font-size: 55px;
        color: #236CFF;

        &:hover {
          background-color: #F3F6FB;
        }

        > svg {
          position: absolute;
          top: 0;
          left: 0;
        }
      }

      > span {
        font-size: 14px;
        color: #000;
        line-height: 20px;
        word-break: keep-all;
      }

      &.on .icon-wrapper {
        color: #fff;
        background-color: #236CFF;
      }

      &.disabled {
        .icon-wrapper {
          cursor: not-allowed;

          > svg {
            opacity: 0.2;
          }

          &:hover {
            background-color: #fff;
          }
        }

        > span {
          opacity: 0.2;
        }
      }
    }

    .file-manager-control{
      position: relative;
      .count {
        position: absolute;
        top: 0;
        right: -12px;
        width: 26px;
        height: 26px;
        background: #FD6565;
        box-shadow: 0px 5px 30px 0px rgba(89,132,219,0.08);
        border: 1px solid #EDEFF2;
        font-size: 12px;
        font-weight: 500;
        color: #FFFFFF;
        line-height: 26px;
        border-radius: 50%;
      }
    }
    .hang-up {
      // position: absolute;
      // top: 20px;
      // right: 63px;

      .icon-wrapper {
        border: 1px solid #FD6565;
        color: #FD6565;
        box-shadow: 0 5px 30px 0 rgba(89, 132, 219, 0.08);

        &:hover {
          color: #fff;
          background-color: #FD6565;
        }
      }
    }
  }

  .two-buttons-one-close-confirm-modal {
    ::v-deep .custom-content {
      padding: 10px;
      margin-bottom: 0;
      position: relative;
      user-select: none;

      > span {
        display: block;
        width: 379px;
        margin-bottom: 82px;
        font-size: 18px;
        font-weight: 500;
        color: #000000;
        line-height: 27px;
      }

      > i {
        display: block;
        width: 22px;
        height: 22px;
        background-image: url(~@/assets/close.svg);
        background-size: 100% 100%;
        position: absolute;
        right: -8px;
        top: -6px;
        cursor: pointer;
      }

      > .basic-btn-group {
        justify-content: flex-end;

        > button {
          margin-right: 32px;

          &:last-child {
            margin-right: 0;
          }
        }
      }
    }
  }

  .screen-warning {
    ::v-deep .custom-content {
      padding: 10px;
      margin-bottom: 0;
      position: relative;

      > span {
        margin-bottom: 12px;
      }

      > .basic-btn-group {
        margin-top: 76px;
      }
    }
  }

  .host-leave-warning {
    ::v-deep .ant-modal{
      min-width: 540px;
      width: auto!important;
    }
    ::v-deep .ant-modal-body {
      padding: 0;

      .custom-content {

        h2 {
          padding: 0 17px 0 32px;
          margin: 0;
          height: 56px;
          border-bottom: 1px solid #EDEFF2;
          display: flex;
          align-items: center;
          justify-content: space-between;

          > span {
            font-size: 18px;
            font-weight: 500;
            color: #000000;
          }

          > i {
            display: block;
            width: 22px;
            height: 22px;
            background-image: url(~@/assets/close.svg);
            background-size: 100% 100%;
            cursor: pointer;
          }
        }

        p {
          padding: 15px 32px 32px;

          > span {
            font-size: 16px;
            color: #000000;
            line-height: 24px;
          }

          > .basic-btn-group {
            margin-top: 38px;
            justify-content: flex-end;

            > button {
              margin-right: 32px;

              &:last-child {
                margin-right: 0;
              }
            }

            button.primary-btn {
              padding: 0 24px
            }
          }
        }
      }
    }
  }

  .drawer-enter-to, .drawer-leave {
    width: 420px;
  }

  .drawer-leave-active, .drawer-enter-active {
    transition: width .3s;
  }

  .drawer-enter, .drawer-leave-to {
    width: 0;
  }

  .fill-remaining-height {
    position: relative;
    flex: 1;
    flex-flow: row nowrap;
    align-items: stretch;
    overflow-x: hidden;
  }

  #session-members,
  #file-manager {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    height: 100%;
    z-index: 4;
  }
}
</style>
